import {
  checkOnboardingTemplateDependencies,
  type OnboardingTemplateDefinition,
  type OnboardingTemplateTask,
} from '@gb-ticket/shared-types';

export const FLOW_CARD_W = 240;
export const FLOW_CARD_H = 72;
export const FLOW_PAD = 16;
export const FLOW_HEADER = 40;
export const FLOW_TASK_GAP = 16;
export const FLOW_FRAME_GAP = 64;
export const FLOW_TASK_STEP = FLOW_CARD_H + FLOW_TASK_GAP;

const CYCLE_MESSAGE = 'Die Aufgabenabhängigkeiten enthalten einen Zyklus.';

export type FlowTask = OnboardingTemplateTask & { x: number; y: number };
export type FlowPhase = {
  id: string;
  title: string;
  sortOrder: number;
  stage: 'preboarding' | 'onboarding';
  x: number;
  y: number;
  tasks: FlowTask[];
};

type Point = { x: number; y: number };

export function frameSize(tasks: readonly Point[]): { width: number; height: number } {
  const width = FLOW_PAD + FLOW_CARD_W + FLOW_PAD;
  const height = FLOW_HEADER + FLOW_PAD;
  if (tasks.length === 0) {
    return { width, height };
  }
  const right = Math.max(...tasks.map((task) => task.x + FLOW_CARD_W));
  const bottom = Math.max(...tasks.map((task) => task.y + FLOW_CARD_H));
  return {
    width: Math.max(width, right + FLOW_PAD),
    height: Math.max(height, bottom + FLOW_PAD),
  };
}

export function hasSavedPositions(definition: OnboardingTemplateDefinition): boolean {
  return definition.phases.every(
    (phase) =>
      phase.x !== undefined &&
      phase.y !== undefined &&
      phase.tasks.every((task) => task.x !== undefined && task.y !== undefined),
  );
}

export function materializeLayout(definition: OnboardingTemplateDefinition): FlowPhase[] {
  const ordered = [...definition.phases].sort((a, b) => a.sortOrder - b.sortOrder);
  if (!hasSavedPositions(definition)) {
    return initialLayout(ordered);
  }
  return ordered.map((phase) => ({
    id: phase.id,
    title: phase.title,
    sortOrder: phase.sortOrder,
    stage: phase.stage,
    x: phase.x ?? 0,
    y: phase.y ?? 0,
    tasks: phase.tasks.map((task) => ({
      ...task,
      x: task.x ?? FLOW_PAD,
      y: task.y ?? FLOW_HEADER + FLOW_PAD,
    })),
  }));
}

export function sortFlow(phases: readonly FlowPhase[]): FlowPhase[] {
  const phaseIndex = new Map(phases.map((phase, index) => [phase.id, index]));
  return [...phases]
    .sort(
      (a, b) => a.y - b.y || a.x - b.x || (phaseIndex.get(a.id) ?? 0) - (phaseIndex.get(b.id) ?? 0),
    )
    .map((phase, index) => {
      const taskIndex = new Map(phase.tasks.map((task, taskAt) => [task.key, taskAt]));
      return {
        ...phase,
        sortOrder: index,
        tasks: [...phase.tasks].sort(
          (a, b) =>
            a.y - b.y || a.x - b.x || (taskIndex.get(a.key) ?? 0) - (taskIndex.get(b.key) ?? 0),
        ),
      };
    });
}

export function moveFrame(
  phases: readonly FlowPhase[],
  phaseId: string,
  x: number,
  y: number,
): FlowPhase[] {
  return phases.map((phase) => (phase.id === phaseId ? { ...phase, x, y } : phase));
}

export function moveTask(
  phases: readonly FlowPhase[],
  taskKey: string,
  absoluteX: number,
  absoluteY: number,
): FlowPhase[] {
  const located = locate(phases, taskKey);
  if (!located) {
    return [...phases];
  }
  const before = frameHeights(phases);
  const centerX = absoluteX + FLOW_CARD_W / 2;
  const centerY = absoluteY + FLOW_CARD_H / 2;
  const host = phases.find((phase) => contains(phase, centerX, centerY));
  if (!host) {
    return phases.map((phase) => ({ ...phase, tasks: [...phase.tasks] }));
  }
  const nextTask = {
    ...located.task,
    x: absoluteX - host.x,
    y: absoluteY - host.y,
  };
  const moved = phases.map((phase) => {
    const without = phase.tasks.filter((task) => task.key !== taskKey);
    if (phase.id !== host.id) {
      return { ...phase, tasks: without };
    }
    return { ...phase, tasks: [...without, nextTask] };
  });
  return clearGrownOverlaps(moved, before);
}

export function addPhase(phases: readonly FlowPhase[]): FlowPhase[] {
  const id = nextPhaseId(phases);
  if (phases.length === 0) {
    return [emptyPhase(id, 0, 0, 0)];
  }
  const last = sortFlow(phases).at(-1);
  if (!last) {
    return [emptyPhase(id, 0, 0, 0)];
  }
  const size = frameSize(last.tasks);
  return pushOverlappingFrames(
    [...phases, emptyPhase(id, phases.length, last.x, last.y + size.height + FLOW_FRAME_GAP)],
    id,
  );
}

export function addTask(
  phases: readonly FlowPhase[],
  phaseId: string | null,
  task: FlowTask,
): FlowPhase[] {
  const base = phases.length === 0 ? addPhase(phases) : [...phases];
  const host = base.find((phase) => phase.id === phaseId) ?? sortFlow(base).at(-1) ?? base[0];
  if (!host) {
    return base;
  }
  const y =
    host.tasks.length === 0
      ? FLOW_HEADER + FLOW_PAD
      : Math.max(...host.tasks.map((item) => item.y + FLOW_CARD_H)) + FLOW_TASK_GAP;
  const next = { ...task, x: FLOW_PAD, y };
  return pushOverlappingFrames(
    base.map((phase) =>
      phase.id === host.id ? { ...phase, tasks: [...phase.tasks, next] } : phase,
    ),
    host.id,
  );
}

export function removePhase(phases: readonly FlowPhase[], phaseId: string): FlowPhase[] {
  const removed = new Set(
    phases
      .filter((phase) => phase.id === phaseId)
      .flatMap((phase) => phase.tasks.map((task) => task.key)),
  );
  return phases
    .filter((phase) => phase.id !== phaseId)
    .map((phase) => ({
      ...phase,
      tasks: phase.tasks.map((task) => ({
        ...task,
        dependsOn: task.dependsOn.filter((key) => !removed.has(key)),
      })),
    }));
}

export function removeTask(phases: readonly FlowPhase[], taskKey: string): FlowPhase[] {
  return phases.map((phase) => ({
    ...phase,
    tasks: phase.tasks
      .filter((task) => task.key !== taskKey)
      .map((task) => ({
        ...task,
        dependsOn: task.dependsOn.filter((key) => key !== taskKey),
      })),
  }));
}

export function linkTasks(
  phases: readonly FlowPhase[],
  sourceKey: string,
  targetKey: string,
): { ok: true; phases: FlowPhase[] } | { ok: false; message: string } {
  const next = phases.map((phase) => ({
    ...phase,
    tasks: phase.tasks.map((task) =>
      task.key === targetKey && !task.dependsOn.includes(sourceKey)
        ? { ...task, dependsOn: [...task.dependsOn, sourceKey] }
        : task,
    ),
  }));
  const graph = checkOnboardingTemplateDependencies(next.flatMap((phase) => phase.tasks));
  if (!graph.ok) {
    return { ok: false, message: graph.message || CYCLE_MESSAGE };
  }
  return { ok: true, phases: next };
}

function initialLayout(phases: OnboardingTemplateDefinition['phases']): FlowPhase[] {
  let cursor = 0;
  return phases.map((phase) => {
    const tasks = phase.tasks.map((task, index) => ({
      ...task,
      x: FLOW_PAD,
      y: FLOW_HEADER + FLOW_PAD + index * FLOW_TASK_STEP,
    }));
    const placed: FlowPhase = {
      id: phase.id,
      title: phase.title,
      sortOrder: phase.sortOrder,
      stage: phase.stage,
      x: 0,
      y: cursor,
      tasks,
    };
    cursor += frameSize(tasks).height + FLOW_FRAME_GAP;
    return placed;
  });
}

function frameHeights(phases: readonly FlowPhase[]): Map<string, number> {
  return new Map(phases.map((phase) => [phase.id, frameSize(phase.tasks).height]));
}

function clearGrownOverlaps(phases: FlowPhase[], before: ReadonlyMap<string, number>): FlowPhase[] {
  return phases.reduce<FlowPhase[]>((current, phase) => {
    if (frameSize(phase.tasks).height <= (before.get(phase.id) ?? 0)) {
      return current;
    }
    return pushOverlappingFrames(current, phase.id);
  }, phases);
}

function pushOverlappingFrames(phases: readonly FlowPhase[], hostId: string): FlowPhase[] {
  const next = phases.map((phase) => ({ ...phase, tasks: [...phase.tasks] }));
  const moved = new Set<string>([hostId]);
  for (let guard = 0; guard < next.length * next.length; guard += 1) {
    let changed = false;
    for (const above of next) {
      if (!moved.has(above.id)) {
        continue;
      }
      const aboveSize = frameSize(above.tasks);
      const aboveBottom = above.y + aboveSize.height;
      for (const below of next) {
        if (below.id === above.id || below.y < above.y) {
          continue;
        }
        const belowSize = frameSize(below.tasks);
        const overlapsX =
          below.x < above.x + aboveSize.width && below.x + belowSize.width > above.x;
        const overlapsY = below.y < aboveBottom && below.y + belowSize.height > above.y;
        if (!overlapsX || !overlapsY) {
          continue;
        }
        below.y = aboveBottom + FLOW_FRAME_GAP;
        moved.add(below.id);
        changed = true;
      }
    }
    if (!changed) {
      return next;
    }
  }
  return next;
}

function contains(phase: FlowPhase, x: number, y: number): boolean {
  const size = frameSize(phase.tasks);
  return x >= phase.x && y >= phase.y && x <= phase.x + size.width && y <= phase.y + size.height;
}

function locate(phases: readonly FlowPhase[], taskKey: string) {
  for (const phase of phases) {
    const task = phase.tasks.find((item) => item.key === taskKey);
    if (task) {
      return { phase, task };
    }
  }
  return null;
}

function emptyPhase(id: string, sortOrder: number, x: number, y: number): FlowPhase {
  return {
    id,
    title: 'Neue Phase',
    sortOrder,
    stage: 'onboarding',
    x,
    y,
    tasks: [],
  };
}

function nextPhaseId(phases: readonly FlowPhase[]): string {
  const ids = new Set(phases.map((phase) => phase.id));
  let index = phases.length + 1;
  let id = `phase_${index}`;
  while (ids.has(id)) {
    index += 1;
    id = `phase_${index}`;
  }
  return id;
}

export function flowToDefinition(phases: readonly FlowPhase[]): OnboardingTemplateDefinition {
  return {
    schemaVersion: 1,
    dueBasis: 'calendar_days',
    phases: sortFlow(phases).map((phase) => ({
      id: phase.id,
      title: phase.title,
      sortOrder: phase.sortOrder,
      stage: phase.stage,
      x: phase.x,
      y: phase.y,
      tasks: phase.tasks.map((task) => ({
        ...task,
        key: task.key.trim(),
        assigneeUserId: task.assignee === 'user' ? (task.assigneeUserId ?? null) : null,
        assigneeRoleId: task.assignee === 'role' ? (task.assigneeRoleId ?? null) : null,
        assigneeDepartmentId:
          task.assignee === 'department' ? (task.assigneeDepartmentId ?? null) : null,
        assigneeTeamId: task.assignee === 'team' ? (task.assigneeTeamId ?? null) : null,
        dataFields: task.kind === 'data' || task.kind === 'review' ? task.dataFields : [],
      })),
    })),
  };
}

export function taskShape(kind: OnboardingTemplateTask['kind']): string {
  switch (kind) {
    case 'confirm':
      return 'filled';
    case 'info':
      return 'outline';
    case 'external_confirm':
      return 'double';
    case 'data':
      return 'lines';
    case 'review':
      return 'check';
    case 'document_provide':
      return 'document-out';
    case 'document_upload':
      return 'document-in';
    case 'asset_issue':
      return 'square-out';
    case 'asset_return':
      return 'square-in';
    case 'vehicle_assign':
      return 'pill-out';
    case 'vehicle_return':
      return 'pill-in';
  }
}
